import React from "react";
import { motion } from "framer-motion";

export const politicalCases = [
  {
    n: "01",
    cat: "Assembly · South Kerala · 2021",
    title: "Flipping a Fortress.",
    summary:
      "Won a three-term opposition seat in under 70 days by re-framing the constituency's story around the incumbent's development failures.",
    challenge:
      "Challenger trailing by 14 percentage points in a seat held by the opposing front for three consecutive terms. Manufacture a credible comeback in under 70 days.",
    strategy: [
      "Malayalam media audit — mapped outlets, editors and access pathways",
      "“People's Issues First” narrative against three constituency-specific development failures",
      "Parallel digital operation seeding community-level conversations",
      "Scripted candidate appearances engineered for quotable lines",
      "Timed governance-gap brief that split incumbent's coverage at the inflection point",
    ],
    outcome:
      "Won the seat by 3,200 votes — flipping a constituency held for 15 years. Name recognition rose from 34% to 79% in eight weeks. Zero negative media incidents.",
    metrics: [
      { v: "+18pt", l: "Poll swing in 9 weeks" },
      { v: "3,200", l: "Vote margin" },
      { v: "130+", l: "Media mentions" },
    ],
    quote:
      "The most dangerous thing in a Kerala election isn't your opponent's money — it's their story. Our job was to make the constituency's story bigger than the incumbent's story. When that happened, the votes followed.",
    quoteRole: "BWK Campaign Director · Anonymous per NDA",
  },
  {
    n: "02",
    cat: "Urban Municipality · Central Kerala · 2020",
    title: "Hung Council to Clear Majority.",
    summary:
      "Engineered a municipal sweep — flipped 5 of 6 targeted swing wards to deliver the front's first clear majority in a decade.",
    challenge:
      "Opposing front held a narrow one-term majority in a tightly contested urban municipality. Goal: win 4–6 swing wards to secure a clear majority.",
    strategy: [
      "Ward-level “issue heat map” identifying the top grievance per target ward",
      "Issue-specific editorial seeded into local cable TV news and ward Facebook groups",
      "Community voices identified and briefed as informal validators",
      "“Shadow candidate” media strategy for compounded visibility",
      "Rapid-response WhatsApp operation to neutralise opposition claims in real time",
    ],
    outcome:
      "Won 5 of 6 targeted swing wards — converted a hung council into a clear majority. The front's first municipal majority in this body in a decade. No PR crises through the cycle.",
    metrics: [
      { v: "5 / 6", l: "Swing wards won" },
      { v: "1st", l: "Majority in a decade" },
      { v: "7w", l: "Campaign window" },
    ],
    quote:
      "Municipal politics in Kerala is hyperlocal in a way no national playbook prepares you for. The road in front of someone's house matters more than the state budget speech. We knew that. The opposition didn't respond until it was too late.",
    quoteRole: "BWK Field Strategy Lead · Anonymous per NDA",
  },
  {
    n: "03",
    cat: "Assembly By-Election · North Kerala",
    title: "Crisis to Comeback.",
    summary:
      "Neutralised an opposition-fed controversy across three Malayalam news channels within 48 hours — three weeks before polling day.",
    challenge:
      "Unverified allegations against the candidate broke across three Malayalam news channels simultaneously, three weeks before polling day. Risk of fatal narrative damage in a high-stakes by-election.",
    strategy: [
      "Immediate origin and seeding-contact assessment to identify the playbook in motion",
      "Verified factual rebuttals and supporting documents assembled within hours",
      "Controlled, off-the-record briefings with key Malayalam editors",
      "Clean 60-second voice note from the candidate seeded into constituency WhatsApp groups",
      "Heavily covered constituency visit pivoted the news cycle back to positive ground",
    ],
    outcome:
      "Story neutralised within 48 hours; zero major follow-up pickups. Candidate approval rose 6 points post-crisis. Seat retained with a comfortable margin.",
    metrics: [
      { v: "48h", l: "Narrative neutralised" },
      { v: "+6pt", l: "Approval post-crisis" },
      { v: "0", l: "Major pickups after 48h" },
    ],
    quote:
      "A fabricated story in Kerala's media ecosystem has a half-life of 72 hours if left alone — and 15 minutes if you handle it correctly. We handled it correctly. The opposition wasted their best ammunition three weeks early. After that, the campaign was ours.",
    quoteRole: "BWK Crisis Response Director · Anonymous per NDA",
  },
];

export const corporateCases = [
  {
    n: "01",
    cat: "Real Estate · NRI Market · Kochi",
    title: "Selling to the Gulf before the brochure reached Kerala.",
    summary:
      "Generated 340+ NRI enquiries and sold out Phase 1 of a luxury Kochi development inside four months — built entirely on editorial credibility, not advertising.",
    challenge:
      "A mid-size Kerala developer launching its first NRI-targeted luxury project. NRIs (30–35% of premium Kochi buyers) decide remotely and need editorial validation, community endorsement and founder credibility — not hoardings.",
    strategy: [
      "Founder profiling — 3 editorial interviews in leading Malayalam business publications + property expo panel",
      "Gulf Malayalam community seeding across 12 associations in Dubai, Abu Dhabi, Sharjah & Qatar (80,000+ reach)",
      "Editorial features in Manorama Online, Mathrubhumi Business and Deepika — no advertorials",
      "Vizhinjam infrastructure narrative linking the project to macroeconomic appreciation thesis",
      "“Real Buyers, Real Reasons” testimonial series profiling 4 early NRI buyers in Gulf-Kerala media",
    ],
    outcome:
      "Phase 1 (48 units) fully booked 3 months ahead of target. Developer's brand awareness in Gulf-Kerala communities moved from near-zero to measurably present in one quarter. MD now featured as a credible voice in Mathrubhumi Business. 12-month retainer signed for Phase 2.",
    metrics: [
      { v: "340+", l: "NRI enquiries in 90 days" },
      { v: "4mo", l: "Phase 1 sold out" },
      { v: "80K+", l: "Gulf-Keralite reach" },
    ],
    quote:
      "In Kerala real estate, advertising rents attention. PR builds trust. NRIs sending crores from Dubai don't buy from a hoarding — they buy from a story they believe in. We told the right story to the right communities before the brochure had even gone to print.",
    quoteRole: "BWK Corporate PR Lead · Anonymous per NDA",
  },
  {
    n: "02",
    cat: "Healthcare · Reputation Recovery · Central Kerala",
    title: "Rebuilding a hospital after a single viral complaint.",
    summary:
      "Converted a viral patient grievance into the hospital's strongest brand-equity moment — OPD footfall grew 28% above pre-crisis levels within six months.",
    challenge:
      "A patient's Facebook post detailing a billing dispute went viral locally — 800+ shares, 200+ comments — and was picked up by a news blog within 12 hours. By the time the hospital called BWK, the narrative already had 48 hours of momentum.",
    strategy: [
      "Empathy-first response architecture — drafted from the Medical Director in warm, Malayalam-native voice",
      "Briefed 6 respected doctors & community figures who organically shared genuine positive experiences",
      "Direct newsroom outreach — updated the news blog into a balanced piece with the hospital's response",
      "Phase 2 rebuild: 8 in-depth editorial features profiling specialist doctors at the hospital",
      "Community health camp series across 5 panchayats — covered by local cable TV for 3 months",
      "“Voices from Recovery” — 6-episode patient story video series; 3 episodes crossed 10K organic views",
    ],
    outcome:
      "OPD footfall grew 28% above pre-crisis levels in six months. Out-of-district specialist bookings up 19%. Hospital signed a 24-month PR retainer. Crisis ended as a net reputational gain, not a loss.",
    metrics: [
      { v: "+28%", l: "OPD footfall growth" },
      { v: "+19%", l: "Out-of-district bookings" },
      { v: "24mo", l: "Retainer signed" },
    ],
    quote:
      "Kerala's healthcare consumers do not forget. But they do forgive — if they see genuine accountability and consistent quality. We gave the hospital a framework to show both. The crisis turned out to be the best PR opportunity they'd had in a decade.",
    quoteRole: "BWK Healthcare PR Specialist · Anonymous per NDA",
  },
  {
    n: "03",
    cat: "FMCG · Brand Launch · North & Central Kerala",
    title: "Zero to shelf in 90 days — without advertising.",
    summary:
      "Generated ₹42 lakh in earned media and 600+ retail listings for a new Kerala FMCG brand in 90 days, against a ₹8L PR spend — a 5.25× media ROI.",
    challenge:
      "A seed-funded premium heritage food brand launching against entrenched national FMCG players. ₹8L PR budget, zero advertising spend. Kerala consumes 10% of India's FMCG output from 2.7% of its population — a discerning, brand-literate market.",
    strategy: [
      "Founder story-first launch in Manorama Sunday, Madhyamam Weekend and two Malayalam YouTube channels — shared 3,400+ times in 48 hours",
      "Non-paid seeding to 22 authentic Kerala food creators (16 produced organic content; 4.8L combined reach for ₹38,000)",
      "Barter partnerships with 4 respected Kerala chefs — 2L+ views, 1,800+ buyer-intent comments",
      "Editorial-grade “brand story dossier” used as demand proof to land orders from 6 premium grocery chains without discounts",
      "“Heritage Kitchen” tasting event — Manorama TV, Asianet and Mediaone all ran segments within 48 hours",
    ],
    outcome:
      "600+ retail outlets listed across Kozhikode, Thrissur, Malappuram and Palakkad. ₹42L earned media against ₹8L PR spend (5.25× ROI). First reorders from premium chains within six weeks. Now in conversation with two national D2C platforms for all-India PR.",
    metrics: [
      { v: "₹42L", l: "Earned media in 90 days" },
      { v: "600+", l: "Retail outlets listed" },
      { v: "5.25×", l: "Return on PR spend" },
    ],
    quote:
      "Kerala consumers are India's most brand-literate. They read ingredient labels. They google founders. They ask the local grocery owner for recommendations. Our job was to make sure every touchpoint in that discovery journey said the same thing — this brand is real, this story is true, this product is worth it.",
    quoteRole: "BWK FMCG Campaign Director · Anonymous per NDA",
  },
];

const CaseList = ({ cases }) => (
  <div className="space-y-10 md:space-y-16">
    {cases.map((c, i) => (
      <motion.article
        key={i}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        data-testid={`case-${i}`}
        className="border border-black bg-white"
      >
        <div className="grid grid-cols-12 border-b border-black">
          <div className="col-span-12 md:col-span-2 p-6 md:p-8 border-b md:border-b-0 md:border-r border-black">
            <div className="font-serif-display italic text-5xl md:text-6xl text-black/40">
              {c.n}
            </div>
          </div>
          <div className="col-span-12 md:col-span-10 p-6 md:p-8 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <div className="overline text-black/60">{c.cat}</div>
              <h3 className="font-serif-display text-3xl md:text-5xl mt-3 leading-[1.02]">
                {c.title}
              </h3>
            </div>
            <span className="text-[0.7rem] uppercase tracking-[0.22em] text-black/50 shrink-0">
              Confidential · Anonymised per NDA
            </span>
          </div>
        </div>

        <div className="p-6 md:p-10 border-b border-black/15">
          <p className="font-serif-display text-xl md:text-2xl leading-snug text-black/80 max-w-4xl italic">
            {c.summary}
          </p>
        </div>

        <div className="grid grid-cols-12">
          <div className="col-span-12 md:col-span-4 p-6 md:p-10 border-b md:border-b-0 md:border-r border-black/15">
            <div className="overline text-black/55 mb-4">The Challenge</div>
            <p className="text-sm md:text-base leading-relaxed text-black/80">
              {c.challenge}
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 p-6 md:p-10 border-b md:border-b-0 md:border-r border-black/15">
            <div className="overline text-black/55 mb-4">The Strategy</div>
            <ul className="space-y-3">
              {c.strategy.map((s, k) => (
                <li
                  key={k}
                  className="text-sm leading-relaxed text-black/80 flex gap-3"
                >
                  <span className="mt-2 inline-block w-1.5 h-1.5 bg-black shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="col-span-12 md:col-span-4 p-6 md:p-10 bg-[#f6f6f4]">
            <div className="overline text-black/55 mb-4">The Outcome</div>
            <p className="text-sm md:text-base leading-relaxed text-black/85">
              {c.outcome}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 border-t border-black bg-[#0a0a0a] text-white">
          {c.metrics.map((m, k) => (
            <div
              key={k}
              className={`p-6 md:p-8 ${
                k < c.metrics.length - 1 ? "border-r border-white/15" : ""
              }`}
            >
              <div className="stat-num text-4xl md:text-6xl tabular">{m.v}</div>
              <div className="mt-3 text-[0.65rem] md:text-[0.7rem] uppercase tracking-[0.2em] text-white/70">
                {m.l}
              </div>
            </div>
          ))}
        </div>

        <figure className="p-6 md:p-10 border-t border-black">
          <blockquote className="font-serif-display text-xl md:text-3xl leading-snug italic text-black/85 max-w-5xl">
            “{c.quote}”
          </blockquote>
          <figcaption className="mt-5 text-[0.7rem] uppercase tracking-[0.22em] text-black/55">
            — {c.quoteRole}
          </figcaption>
        </figure>
      </motion.article>
    ))}
  </div>
);

const CaseStudies = () => {
  return (
    <section
      id="work"
      data-testid="case-studies-section"
      className="section-gutter py-20 md:py-32 border-t border-black/10"
    >
      {/* Political block */}
      <div className="grid grid-cols-12 gap-6 mb-12 md:mb-20">
        <div className="col-span-12 md:col-span-7">
          <div className="overline text-black/60">Chapter 04 — Dossier · Political</div>
          <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-6 leading-[0.92]">
            Political <em>case files.</em>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
          <p className="text-sm md:text-base text-black/70 leading-relaxed">
            Three live mandates from Kerala — anonymised per NDA. The numbers,
            timelines and tactics are real.
          </p>
        </div>
      </div>

      <CaseList cases={politicalCases} />

      {/* Corporate block */}
      <div className="grid grid-cols-12 gap-6 mt-24 md:mt-32 mb-12 md:mb-20 pt-16 border-t border-black/15">
        <div className="col-span-12 md:col-span-7">
          <div className="overline text-black/60">Chapter 04 — Dossier · Corporate</div>
          <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-6 leading-[0.92]">
            Corporate <em>case files.</em>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
          <p className="text-sm md:text-base text-black/70 leading-relaxed">
            Brand launches, reputation recoveries and market entries across
            real estate, healthcare and FMCG — PR that moved markets, not just
            mentions.
          </p>
        </div>
      </div>

      <CaseList cases={corporateCases} />
    </section>
  );
};

export default CaseStudies;
